<template>
  <div class="bg-white px-6 py-2 text-sm text-gray-400">
    <div v-if="isMonday" class="pb-4">
      <select id="combobox" v-model="selectedWeek"
        class="peer block w-full rounded-md border-0 pb-1.5 pt-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-secondary sm:text-sm sm:leading-6 font-medium"
        role="combobox" aria-controls="options" aria-expanded="false"
        @change="onWeekChange()">
        <option v-for="week in weekOptions" :key="week.value" :value="week.value">{{ week.display }}</option>
      </select>
      <div v-if="selectedWeek !== weekNumber">
        <div class="text-red-600">
          Indberetning af forrige uge er kun muligt frem til klokken 10:00
        </div>
      </div>
    </div>
    <div v-else>
      <h1 class="text-xl font-medium text-secondary pb-4">
        Uge {{ weekNumber }}
      </h1>
    </div>
    <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
      <li v-for="day in reportWeek.reportWeek" :key="day.dayNumber" class="m-0 flex items-center p-0 cursor-pointer">
        <a @click.prevent="routeToReport(getFullDateForDay(year, selectedWeek, day.dayNumber))" v-if="day.project" class="flex w-full gap-x-4 no-underline py-1.5">
          <div class="flex items-center shrink-0">
            <div :class="['w-4 h-4 rounded-full', day.timeSpent ? 'bg-emerald-700' : 'bg-gray-200']"></div>
          </div>
          <div class="flex flex-col grow">
            <div class="text-sm font-medium text-blue-900">{{ weekDays[day.dayNumber - 1] }} - {{ day.project ? day.project.projectName + ' - ' + day.project.customerName : 'Fri' }}</div>
            <div class="text-base font-medium leading-5 text-gray-800 font-semibold">{{ day.timeSpent ? `${day.timeSpentWithGap ?? day.timeSpent} timer` : '-'}}</div>
            <div class="text-base font-medium leading-5 text-gray-800" :v-if="day.remark">{{day.remark}}</div>
          </div>
          <div class="flex items-center shrink-0">
            <svg class="w-8" viewBox="0 0 33 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20.029 20.4773L14.5631 25.9432C14.2995 26.2068 13.8721 26.2068 13.6085 25.9432L12.971 25.3057C12.7079 25.0425 12.7073 24.616 12.9699 24.3522L17.3017 20L12.9699 15.6478C12.7073 15.384 12.7079 14.9575 12.971 14.6943L13.6085 14.0568C13.8721 13.7932 14.2995 13.7932 14.5631 14.0568L20.029 19.5227C20.2926 19.7863 20.2926 20.2137 20.029 20.4773Z"
                fill="#E5E7EB" />
            </svg>
          </div>
        </a>
        <span v-if="!day.project" class="flex w-full gap-x-4 no-underline py-1.5">
          <div class="flex items-center shrink-0">
            <div :class="['w-4 h-4 rounded-full', day.timeSpent ? 'bg-emerald-700' : 'bg-gray-200']"></div>
          </div>
          <div class="flex flex-col grow">
            <div class="text-sm font-medium text-blue-900">{{ weekDays[day.dayNumber - 1] }} - {{ 'Fri' }}</div>
            <div class="text-base font-medium leading-5 text-gray-800">{{ day.timeSpent ? `${day.timeSpentWithGap ?? day.timeSpent} timer` : '-' }}</div>
          </div>
          <div class="flex items-center shrink-0">
            <svg class="w-8" viewBox="0 0 33 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20.029 20.4773L14.5631 25.9432C14.2995 26.2068 13.8721 26.2068 13.6085 25.9432L12.971 25.3057C12.7079 25.0425 12.7073 24.616 12.9699 24.3522L17.3017 20L12.9699 15.6478C12.7073 15.384 12.7079 14.9575 12.971 14.6943L13.6085 14.0568C13.8721 13.7932 14.2995 13.7932 14.5631 14.0568L20.029 19.5227C20.2926 19.7863 20.2926 20.2137 20.029 20.4773Z"
                fill="#E5E7EB" />
            </svg>
          </div>
        </span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { getWeekNumber, toJSONDate } from '@/core/utils'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ReportType } from '@/models/report.model'

export default defineComponent({
  name: 'WeeklyActivityLog',
  setup () {
    const router = useRouter()
    const store = useStore()
    const weekNumber = getWeekNumber(new Date())
    const year = new Date().getFullYear()
    const weekDays = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag']

    const isMonday = computed(() => {
      const currentDate = new Date()
      return currentDate.getDay() === 1
    })

    const selectedWeek = ref(weekNumber)
    const weekOptions = computed(() => {
      // Current date
      const currentDate = new Date();

      const currentWeek = getWeekNumber(currentDate)
      currentDate.setDate(currentDate.getDate() - 7)

      const previousWeek = getWeekNumber(currentDate)
      return [
        { value: currentWeek, display: `Uge ${currentWeek}` },
        { value: previousWeek, display: `Uge ${previousWeek}` }
      ]
    })

    onMounted(async () => {
      await store.dispatch('report/getCurrentReportWeekSummary', { weekNumber, year })
    })

    const reportWeek = computed(() => {
      return store.getters['report/reportWeek']
    })

    const reportType = computed(() => {
      return store.getters['report/reportType']
    })

    const onWeekChange = async () => {
      await store.dispatch('report/getCurrentReportWeekSummary', { weekNumber: selectedWeek.value, year })
    }

    function getFullDateForDay(year: number, weekNumber: number, dayNumber: number): Date {
      if (weekNumber === 1 && new Date().getMonth() === 11) {
        year += 1; // Handle week 1 of next year
      }

      // The first day of the year
      const firstDayOfYear = new Date(year, 0, 1);
      // Determining the day of the week for January 1st
      const firstDayOfWeek = firstDayOfYear.getDay();

      // Calculating the offset to align the first week's start to Monday according to ISO 8601
      let daysOffset = 1 - firstDayOfWeek;
      if (firstDayOfWeek === 0) daysOffset = -6; // Sunday
      if (firstDayOfWeek > 4) daysOffset -= 7; // Friday or Saturday

      // Calculating the day number of the year from the week number and day number
      const dateOffset = daysOffset + (weekNumber - 1) * 7 + dayNumber;

      // Constructing the full date
      const fullDate = new Date(year, 0, dateOffset);

      console.log(`fullDate from year ${year}, week ${weekNumber}, day ${dayNumber}: `, fullDate);

      return fullDate;
    }

    const routeToReport = (date: Date) => {
      store.commit('report/SET_REPORT_SELECTED_DATE', date)
      switch (reportType.value) {
        case ReportType.TIME:
          router.push('/reports/create/time')
          break
        case ReportType.TRANSPORT:
          router.push('/reports/create/transport')
          break
        case ReportType.SICK:
          router.push('/reports/create/sick')
          break
      }
    }

    return { weekNumber, weekDays, reportWeek, getFullDateForDay, routeToReport, isMonday, selectedWeek, weekOptions, onWeekChange, year }
  }
})
</script>
